import type {
	DynamicFormSchema,
	FieldType,
} from "@/lib/dynamicForms/interfaces/dynamicForm.interface";
import { persistedFormFieldToDefinition } from "@/lib/dynamicForms/utils/persisted-field-definition.util";
import {
	type LayoutElement,
	LayoutTypes,
} from "@/lib/layoutRenderer/schemas/layout.schema";
import type { FormEditorEntity } from "../form-editor-entity.types";
import type { PaletteFieldType, TicketTypeFormBuilderState } from "../types";
import type { FormRule } from "../types/form-rule-types";
import { projectBuilderFieldToFormField } from "./palette-form-field-factory";
import { resolveBuilderSectionLabel } from "./resolve-builder-section-label";
/**
 * Maps a builder palette id to {@link FieldType} for {@link FieldLayoutElement.layoutInfo.fieldType}.
 * Used so the layout can distinguish UI that shares the same persisted `dataType` (e.g. `textarea` vs `text`, `checkbox` vs `toggleButton`).
 */
export function paletteFieldTypeToLayoutFieldType(
	palette: PaletteFieldType,
): FieldType {
	switch (palette) {
		case "email":
			return "text";
		case "checkbox":
			return "checkbox";
		case "text":
		case "textarea":
		case "number":
		case "phone":
		case "url":
		case "date":
		case "time":
		case "dateTime":
		case "select":
		case "radio":
		case "file":
		case "multiSelect":
		case "users":
		case "signature":
		case "address":
			return palette;
		default: {
			const _exhaustive: never = palette;
			return _exhaustive;
		}
	}
}

/**
 * Maps builder state to {@link DynamicFormSchema} for preview and persistence.
 * Produces `fieldDefinitions` and a JSON Forms–style `layoutJson` root ({@link LayoutElement}) for preview and API serialization.
 */
export function builderStateToDynamicFormSchema(
	state: Pick<
		TicketTypeFormBuilderState,
		"rootIds" | "nodes" | "formTitle" | "formDescription"
	>,
	formId: string,
	options: {
		previewDisabled?: boolean;
		formEditorEntity?: FormEditorEntity;
		/** Cross-field rules to attach as `schema.rules` so the runtime evaluator fires them in preview / live forms. */
		formRules?: FormRule[];
	} = {},
): DynamicFormSchema {
	const formEditorEntity = options.formEditorEntity ?? "ticket";
	const fieldDefinitions: DynamicFormSchema["fieldDefinitions"] = [];
	const seenIds = new Set<string>();

	function addDefinitionFromFieldNode(fieldId: string): void {
		if (seenIds.has(fieldId)) return;
		const node = state.nodes[fieldId];
		if (!node || node.kind !== "field") return;
		const formField = projectBuilderFieldToFormField(
			node.field,
			options.previewDisabled,
			formEditorEntity,
		);
		fieldDefinitions.push(persistedFormFieldToDefinition(formField));
		seenIds.add(fieldId);
	}

	const layoutElements: LayoutElement[] = [];

	for (const rid of state.rootIds) {
		const node = state.nodes[rid];
		if (!node) continue;

		if (node.kind === "section") {
			for (const fid of node.childFieldIds) {
				addDefinitionFromFieldNode(fid);
			}

			const needsAccordion = node.section.collapsible;

			const childLayoutElements: LayoutElement[] = [];
			for (const fid of node.childFieldIds) {
				const fn = state.nodes[fid];
				if (fn?.kind === "field") {
					childLayoutElements.push({
						type: LayoutTypes.Field,
						fieldDefinitionId: fn.field.id,
						layoutInfo: {
							fieldType: paletteFieldTypeToLayoutFieldType(fn.field.fieldType),
						},
					});
				}
			}

			layoutElements.push({
				type: LayoutTypes.Group,
				id: node.section.id,
				label: resolveBuilderSectionLabel(node.section.label, formEditorEntity),
				enabled: needsAccordion ? node.section.collapsible : false,
				defaultExpanded: node.section.defaultExpanded,
				elements: childLayoutElements,
			});
		} else if (node.kind === "field") {
			addDefinitionFromFieldNode(node.field.id);
			layoutElements.push({
				type: LayoutTypes.Field,
				fieldDefinitionId: node.field.id,
				layoutInfo: {
					fieldType: paletteFieldTypeToLayoutFieldType(node.field.fieldType),
				},
			});
		}
	}

	const layoutJson: LayoutElement = {
		type: LayoutTypes.VerticalLayout,
		elements: layoutElements,
	};

	return {
		id: formId,
		title: state.formTitle,
		description: state.formDescription,
		fieldDefinitions,
		layoutJson,
		...(options.formRules && options.formRules.length > 0
			? { rules: options.formRules }
			: {}),
	};
}
